import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { hubPlanAPI } from '../api/hubPlan';
import { availableModules } from '../utils/moduleAccess';

const ModuleContext = createContext(null);

/**
 * The module the person is working in. Which modules they can open follows their role's
 * permissions, then the business's One Manager plan and setup (utils/moduleAccess.js).
 */
export const ModuleProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [activeModule, setActiveModuleState] = useState(null);
  const [loading, setLoading] = useState(true);
  // The business's plan from One Manager: null until known, or when the hub doesn't manage it
  const [plan, setPlan] = useState(null);
  const [planLoaded, setPlanLoaded] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setPlan(null);
      setPlanLoaded(false);
      return undefined;
    }
    let cancelled = false;
    hubPlanAPI
      .get()
      .then((response) => !cancelled && setPlan(response?.data ?? null))
      .catch(() => !cancelled && setPlan(null)) // the API still enforces the plan
      .finally(() => !cancelled && setPlanLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id]);

  // The modules this person can open in this business
  const getUserModules = () => availableModules(user, plan);

  // Restore the module they were in once the plan is known; one module needs no choice
  useEffect(() => {
    if (isAuthenticated && user && !planLoaded) return;
    if (isAuthenticated && user) {
      const savedModule = localStorage.getItem('activeModule');
      const allowedModules = getUserModules();

      if (savedModule && allowedModules.includes(savedModule)) {
        setActiveModuleState(savedModule);
      } else {
        if (savedModule) localStorage.removeItem('activeModule');
        setActiveModuleState(allowedModules.length === 1 ? allowedModules[0] : null);
      }
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getUserModules follows user and plan
  }, [isAuthenticated, user, planLoaded, plan]);

  // Check if user can choose between modules
  const canChooseModule = () => {
    return getUserModules().length > 1;
  };

  // Check if user needs to select a module (for routing)
  const needsModuleSelection = () => {
    return canChooseModule() && !activeModule;
  };

  // Set active module and persist to localStorage
  const setActiveModule = (module) => {
    const allowedModules = getUserModules();
    if (allowedModules.includes(module)) {
      setActiveModuleState(module);
      localStorage.setItem('activeModule', module);
      return true;
    }
    return false;
  };

  // Clear module selection (used on logout)
  const clearModule = () => {
    setActiveModuleState(null);
    localStorage.removeItem('activeModule');
  };

  // Where to go after signing in: the module selection page with a choice to make, else the module
  const getDefaultRedirect = () => {
    if (needsModuleSelection()) {
      return '/select-module';
    }

    const module = activeModule || getUserModules()[0] || 'retail';
    return `/${module}/dashboard`;
  };

  // Check if current user can access a specific module
  const canAccessModule = (module) => {
    return getUserModules().includes(module);
  };

  const value = {
    activeModule,
    setActiveModule,
    clearModule,
    getUserModules,
    canChooseModule,
    needsModuleSelection,
    getDefaultRedirect,
    canAccessModule,
    // Module decisions wait for the plan, so nobody is sent to a module it doesn't include
    loading: loading || (isAuthenticated && !planLoaded)
  };

  return <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>;
};

export const useModule = () => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModule must be used within a ModuleProvider');
  }
  return context;
};

export default ModuleContext;
